<template>
  <v-app v-if="user">
    <!-- App bar for mobile -->
    <v-app-bar
        color="secondary"
        v-if="isMobile"
        max-height="56"
    >

      <!-- Toggle drawer -->
      <v-btn icon @click="toggleDrawer">
        <v-icon class="white--text">mdi-menu</v-icon>
      </v-btn>

      <!-- Logo -->
      <v-img
          src="@/assets/images/logos/logo.png"
          contain
          aspect-ratio="1.5"
      ></v-img>

      <v-spacer></v-spacer>

      <!-- Balance information -->
      <v-chip
          color="primary"
          v-if="isUnlimitedBalance"
      >
        Saldo ilimitado
      </v-chip>

      <v-chip
          color="primary"
          v-else
      >
        {{ isBalanceLoading ? 'Cargando...' : `Saldo: $${balance.toFixed(2)}` }}
      </v-chip>

      <!-- Logout -->
      <v-btn icon @click="openDialogLogout">
        <v-icon class="white--text">mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Navigation drawer -->
    <v-navigation-drawer
        v-model="drawer"
        :temporary="isMobile"
        app
        color="secondary"
    >

      <!-- User info -->
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="text-h6 white--text">
            Hola, {{ user.name || "Usuario" }}
          </v-list-item-title>
          <v-list-item-subtitle class="white--text">
            {{ user.office ? `DIETEC ${user.office}` : textRole }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="secondary darken-1"></v-divider>

      <!-- Navigation options -->
      <v-list nav>
        <v-list-item
            active-class="primary"
            v-for="(item, index) in navigationMenuItems"
            :key="index"
            :to="item.route"
            @click="updateBalance"
        >
          <v-list-item-icon>
            <v-icon class="white--text">{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="white--text">{{ item.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <v-divider
            v-if="!isAdmin"
            class="secondary darken-1"
        ></v-divider>
        <!-- Royalties -->
        <div
            class="px-2 py-2"
            v-if="!isAdmin"
        >
          <v-alert
              outlined
              color="primary"
              text
              dense
              border="left"
              class="mb-0 text-center"
          >
            <h5>Regalías del mes anterior</h5>
            <v-chip
                outlined
                small
                class="text-center me-2"
                :color="royaltiesStatusColor"
                label
            >
              {{ royaltiesStatus }}
            </v-chip>
            <v-chip
                outlined
                small
                class="text-center"
                color="warning"
            >
              ${{ royalties.toFixed(2) }}
            </v-chip>
          </v-alert>
        </div>
        <!-- Logo -->
        <v-divider class="secondary darken-1"></v-divider>
        <div class="px-3">
          <v-img
              :src="imgLogo"
              contain
              aspect-ratio="2.5"
          ></v-img>
        </div>
      </template>
    </v-navigation-drawer>

    <DialogLogout ref="dialogLogout"/>

    <!-- Main content -->
    <v-main
        class="grey lighten-4 background-image"
    >
      <!-- Balance information -->
      <v-chip
          color="primary"
          class="top-right-2 text-h6"
          v-if="!isMobile && isUnlimitedBalance"
      >
        Saldo ilimitado
      </v-chip>

      <v-chip
          color="primary"
          class="top-right-2 text-h6"
          v-else-if="!isMobile && !isUnlimitedBalance"
      >
        {{ isBalanceLoading ? 'Cargando...' : `Saldo: $${balance.toFixed(2)}` }}
      </v-chip>

      <!-- Logout button for desktop -->
      <v-btn
          fab
          color="secondary"
          @click="openDialogLogout"
          v-if="!isMobile"
          small
          class="top-right"
      >
        <v-icon>mdi-logout</v-icon>
      </v-btn>
      <router-view/>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue'
import DialogLogout from "@/components/DialogLogout.vue";
import Swal, {SweetAlertOptions} from "sweetalert2";

export default Vue.extend({
  name: 'AuthenticatedLayout',
  components: {
    DialogLogout
  },
  computed: {
    user() {
      return this.$store.getters['auth/getUser']
    },
    isAdmin(): boolean {
      return this.user.role !== 'DIETICIAN';
    },
    balance() {
      return this.$store.getters['balance/getCurrentBalance']
    },
    isUnlimitedBalance() {
      return this.$store.getters['balance/isUnlimitedBalance']
    },
    isBalanceLoading() {
      return this.$store.getters['balance/isBalanceLoading']
    },
    permissions() {
      return this.$store.getters['permissions/getPermissionsList']
    },
    navigationMenuItems() {
      return this.$store.getters['permissions/getNavigationMenu']
    },
    hasShownLowBalanceAlert() {
      return this.$store.getters['balance/hasShownLowBalanceAlert']
    },
    hasShownRenovationMessage() {
      return this.$store.getters['auth/hasShownRenovationMessage']
    },
    hasShownBirthdayMessage() {
      return this.$store.getters['auth/hasShownBirthdayMessage']
    },
    textRole() {
      if (!this.user) return '';
      switch (this.user.role) {
        case 'SUPERADMIN':
          return 'Administrador General';
        case 'ADMIN':
          return 'Administrador';
        case 'DIETICIAN':
          return `Dietista`;
        default:
          return ''
      }
    },
    royalties() {
      return this.$store.getters['balance/getCurrentRoyalties'];
    },
    royaltiesStatus() {
      const status = this.$store.getters['balance/getRoyaltiesStatus'];
      switch (status) {
        case 'PENDING':
          this.royaltiesStatusColor = 'yellow';
          return 'Pendiente de pago';
        case 'PAID':
          this.royaltiesStatusColor = 'success';
          return 'Pagado';
        case 'EXEMPT':
          this.royaltiesStatusColor = 'white';
          return 'Exento de pago';
        case 'NOT_APPLICABLE':
          this.royaltiesStatusColor = 'secondary';
          return 'No aplica';
        default:
          return 'Desconocido';
      }
    },
    isRoyaltiesLoading() {
      return this.$store.getters['balance/isRoyaltiesLoading'];
    }
  },
  data() {
    return {
      drawer: true,
      imgLogo: require('@/assets/images/logos/logo.png') as string,
      isMobile: false,
      royaltiesStatusColor: 'primary',
    }
  },

  watch: {
    balance(newBalance) {
      if (this.isAdmin) return;
      const MINIMUM_BALANCE_THRESHOLD = 600;

      if (newBalance < MINIMUM_BALANCE_THRESHOLD && !this.hasShownLowBalanceAlert) {
        this.$store.commit('balance/SET_HAS_SHOWN_LOW_BALANCE_ALERT', true);

        this.queueAlert({
          icon: 'warning',
          titleText: 'Aviso importante',
          text: `Tu saldo actual es de $${newBalance.toFixed(2)}, el cual es inferior al mínimo recomendado de $${MINIMUM_BALANCE_THRESHOLD.toFixed(2)}. Por favor, recarga tu saldo para evitar interrupciones en el servicio.`,
          confirmButtonText: 'Entendido',
          allowEscapeKey: false,
          allowOutsideClick: false,
          background: '#fff',
          color: '#000',
          iconColor: '#ff6600',
          confirmButtonColor: '#ff6600',
          position: 'center',
          target: '.v-application',
        });
      }
    },
    user() {
      if (this.user.renovationDate) {
        this.checkRenovation();
      }

      if (this.user.isBirthday) {
        this.checkBirthday();
      }
    }
  },

  methods: {
    openDialogLogout() {
      (this.$refs.dialogLogout as any).dialog = true;
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth < 1264;
    },
    updateBalance() {
      this.$store.dispatch('balance/updateBalance');
    },
    getUserPermissions() {
      this.$store.dispatch('permissions/getPermissionsList');
    },
    getNavigationMenu() {
      this.$store.dispatch('permissions/getNavigationMenu');
    },
    queueAlert(options: SweetAlertOptions) {
      (this as any).alertQueue = (this as any).alertQueue.then(() => {
        return Swal.fire(options);
      })
    },
    checkRenovation(): void {
      const today = new Date();
      const renovationDate = new Date(this.user.renovationDate);
      const timeDiff = renovationDate.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (daysDiff <= 20 && !this.hasShownRenovationMessage) {
        const [year, month, day] = this.user.renovationDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        };
        const formattedRenovationDate = new Intl.DateTimeFormat('es-MX', options).format(date);
        this.$store.commit('auth/SET_HAS_SHOWN_RENOVATION_MESSAGE', true);

        this.queueAlert({
          icon: 'info',
          titleText: 'Renovación próxima',
          text: `Tu contrato se debe renovar antes del ${formattedRenovationDate}. Por favor, contacta con la administración para más información sobre la renovación de tu contrato.`,
          confirmButtonText: 'Entendido',
          allowEscapeKey: false,
          allowOutsideClick: false,
          background: '#fff',
          color: '#000',
          iconColor: '#4CAF50',
          confirmButtonColor: '#4CAF50',
          position: 'center',
          target: '.v-application',
        });
      }
    },
    checkBirthday(): void {
      if (!this.hasShownBirthdayMessage) {
        this.$store.commit('auth/SET_HAS_SHOWN_BIRTHDAY_MESSAGE', true);

        this.queueAlert({
          icon: 'success',
          titleText: '¡Feliz cumpleaños!',
          text: `¡Te deseamos un feliz cumpleaños, ${this.user.name}! Esperamos que tengas un día maravilloso lleno de alegría y sorpresas agradables. Agradecemos tu esfuerzo para hacer de este mundo un lugar más sano.`,
          confirmButtonText: 'Gracias',
          allowEscapeKey: false,
          allowOutsideClick: false,
          background: '#fff',
          color: '#000',
          iconColor: '#E91E63',
          confirmButtonColor: '#E91E63',
          position: 'center',
          target: '.v-application',
        });
      }
    }
  },

  created() {
    this.updateBalance();
    this.checkScreenSize();
    this.getUserPermissions();
    this.getNavigationMenu();
    (this as any).alertQueue = Promise.resolve();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
})
</script>

<style scoped>
.top-right {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.top-right-2 {
  position: absolute;
  top: 15px;
  right: 70px;
  z-index: 2;
}

.background-image {
  position: relative;
  background-image: url('@/assets/images/backgrounds/background-texture.svg');
  background-repeat: repeat;
  background-position: top left;
  background-size: auto;
  min-height: 100vh;
}
</style>
